import { Component, Input, Output } from '@angular/core';
import { UpdateErrorDisplay } from '../update-selected-modal/update-selected-modal.component';
import { BehaviorSubject } from 'rxjs';
import { UpdateResultSummary } from '../../../interfaces/update-vehicles-action.interface';
import { TranslocoDirective } from '@jsverse/transloco';
import { ErrorIconComponent, SpinnerComponent } from 'remarketing-ui-components-lib';
import { CommonModule } from '@angular/common';
import { SaComponent } from 'smartauction-frontend-micro-library';
@Component({
    selector: 'app-multi-result-modal',
    templateUrl: './multi-result-modal.component.html',
    styleUrls: ['./multi-result-modal.component.scss'],
    imports: [SpinnerComponent, CommonModule, TranslocoDirective, ErrorIconComponent]
})
export class MultiResultModalComponent extends SaComponent {
  private selectFailedSubject = new BehaviorSubject<MultiResultContentBase>(undefined);
  private resetTemplateInputSubject = new BehaviorSubject<boolean>(false);
  private closeSubject = new BehaviorSubject<boolean>(false);

  @Output() close$  = this.closeSubject.asObservable();
  @Output() selectFailed$ = this.selectFailedSubject.asObservable();
  @Output() resetGroundingModalTemplate$ = this.resetTemplateInputSubject.asObservable();
  @Input() modalTemplateLabels: MultiResultModalTemplateLabels;
  @Input() isLoading: boolean;
  @Input() templateContent = {
    visibleTemplate: MultiResultModalTemplate.INITIAL_LOADING_TEMPLATE,
    happyPathData: undefined,
    errorPathData: undefined
  } as MultiResultContentBase;

  multiResultModalTemplateEnum = MultiResultModalTemplate;
  templatePunctuation = {
    period: '.',
    parenthesis: '(',
    closingParenthesis: ')',
    colonWithSpace: ': '
  };

  constructor() {
    super();
  }

  selectFailedVehicles(): void {
    this.selectFailedSubject.next(this.templateContent);
  }

  makeTemplateInputDefault(): void {
    this.resetTemplateInputSubject.next(true);
  }

  get headerText(): string {
    return this.isHeaderTextAvailable ? this.templateContent.headerText : '';
  }

  get successMessage(): string {
    const happyPath = this.templateContent?.happyPathData;
    return happyPath?.successMessage?.length ? happyPath.successMessage : '';
  }

  get failureLabelSingular(): string {
    const happyPath = this.templateContent?.happyPathData;
    return happyPath?.failureLabelSingular?.length ? happyPath?.failureLabelSingular : '';
  }

  get failureLabelPlural(): string {
    const happyPath = this.templateContent?.happyPathData;
    return happyPath?.failureLabelPlural?.length ? happyPath?.failureLabelPlural : '';
  }

  get errorMessage(): string {
    const errorPath = this.templateContent?.errorPathData;
    return errorPath?.errorMessage?.length ? errorPath.errorMessage : '';
  }

  get successVins(): string {
    const happyPath = this.templateContent?.happyPathData;
    return happyPath?.successVins?.length ? happyPath.successVins : '';
  }

  get isSuccessVins(): boolean {
    const successes = this?.templateContent?.updateSummary?.successes;
    return successes !== undefined && successes !== null && successes.length > 0;
  }

  get isHeaderTextAvailable(): boolean {
    return !!this.templateContent?.headerText?.trim();
  }

  get isFailureVins(): boolean {
    const failures = this?.templateContent?.updateSummary?.failures;
    return failures !== undefined && failures !== null && failures.length > 0;
  }

  handleCloseModal(): void {
    this.setDefaultTemplate();
    this.makeTemplateInputDefault();
    this.closeSubject.next(true);
  }

  setDefaultTemplate(): void {
    this.templateContent = {
      visibleTemplate: MultiResultModalTemplate.INITIAL_LOADING_TEMPLATE,
      happyPathData: undefined,
      errorPathData: undefined
    } as MultiResultContentBase;
  }
}

export enum MultiResultModalTemplate {
  INITIAL_LOADING_TEMPLATE = 'initialLoadingTemplate',
  RESULTS_TEMPLATE = 'resultsTemplate',
  ERROR_TEMPLATE = 'errorTemplate'
}

export interface MultiResultContentBase {
  visibleTemplate: MultiResultModalTemplate;
  selectedVehicleCount: number;
  updateSummary: UpdateResultSummary;
  updateErrorDisplays: UpdateErrorDisplay[];
  headerText?: string;
  happyPathData?: MultiResultContentHappyPath;
  errorPathData?:MultiResultContentErrorPath;
}

export interface MultiResultContentHappyPath {
  successMessage: string;
  successVins: string;
  failureLabelSingular: string;
  failureLabelPlural: string;
}

export interface MultiResultContentErrorPath {
  errorMessage?: string;
}

export interface MultiResultModalTemplateLabels {
  modalName?: string;
  headline?: string;
  resultSummaryLabel?: string;
  close?: string;
  closeAndSelectFailed?: string;
}
